import Vue from 'vue';
import Router from 'vue-router';

import state from './state';
import { closeBillAcceptor } from './services/device.service';
import Sport from './pages/sport/sport.vue';

Vue.use(Router);
const withTheme = callback => Vue.callWithTheme(callback);

export async function createRouter(routeHandlers) {
    const themeName = Vue.themeName;

    const WelcomeLinks = (await import(`./pages/welcome-links/${themeName}/welcome-links.vue`)).default;
    const Frame = withTheme(t => import(`./pages/frame/frame.vue`));
    const Home = withTheme(t => import(`./pages/home/${t}/home.vue`));

    const LobbyLayout = (await import(`./layouts/lobby/${themeName}/lobby.layout.vue`)).default;
    const GameLayout = (await import(`./layouts/game/${themeName}/game.layout.vue`)).default;
    const SportLayout = withTheme(t => import(`./layouts/sport/${t}/sport.layout.vue`));

    const Lobby = (await import(`./pages/lobby/${themeName}/lobby.vue`)).default;
    const Game = (await import(`./pages/game/${themeName}/game.vue`)).default;
    const GameRedpipe = (await import(`./components/games/redpipe/${themeName}/redpipe.vue`)).default;
    const GameHdRaces = (await import(`./components/games/hdraces/${themeName}/hdraces.vue`)).default;
    const GameFlg = (await import(`./components/games/flg/${themeName}/flg.vue`)).default;
    const GameAviatrix = (await import(`./components/games/aviatrix/aviatrix.vue`)).default;
    const Info = (await import(`./pages/info/${themeName}/info.vue`)).default;
    const OutOfService = withTheme(t => import(`./pages/out-of-service/${t}/out-of-service.vue`));

    const PublicOffer = (await import(`./pages/info/data/PublicOffer.vue`)).default;
    const CasinoLicense = (await import(`./pages/info/data/CasinoLicense.vue`)).default;
    const LotteryLicense = (await import(`./pages/info/data/LotteryLicense.vue`)).default;
    const SportsBetting = (await import(`./pages/info/data/SportsBetting.vue`)).default;

    let router = new Router({
        // NOTE: our product could be integrated on 3thd site
        mode: 'history',
        linkActiveClass: 'is-active',
        scrollBehavior(to, from, savedPosition) {
            // NOTE: Returning the savedPosition will result in a native- like behavior when navigating with back/ forward buttons
            if (savedPosition) return savedPosition;
            else return { x: 0, y: 0 };
        },

        routes: [
            {
                path: '/',
                name: 'home',
                component: Home,
                meta: { requiresAuth: false }
            },
            {
                path: '/menu',
                name: 'menu',
                component: WelcomeLinks
            },
            {
                path: '/frame',
                name: 'frame',
                component: Frame,
                props: true
            },
            {
                path: '/error',
                name: 'error',
                component: OutOfService
            },
            {
                path: '/sport',
                component: SportLayout,
                props: true,
                children: [
                    {
                        path: '',
                        component: Sport
                    },
                    {
                        path: 'home',
                        name: 'sport-home',
                        component: Sport
                    },
                    {
                        path: 'prematch(/[^$]+)?',
                        name: 'sport-prematch',
                        component: Sport
                    },
                    {
                        path: 'live(/[^$]+)?',
                        name: 'sport-live',
                        component: Sport
                    },
                    {
                        path: 'favorites(/[^$]+)?',
                        name: 'sport-favorites',
                        component: Sport
                    },
                    {
                        path: 'results(/[^$]+)?',
                        name: 'sport-results',
                        component: Sport
                    },
                    {
                        path: 'search(/[^$]+)?',
                        name: 'sport-search',
                        component: Sport
                    },
                    {
                        path: 'lotto(/[^$]+)?',
                        name: 'sport-lottery',
                        component: Sport
                    }
                ]
            },
            {
                path: '/',
                component: LobbyLayout,
                props: true,
                children: [
                    {
                        path: '/lobby/:category?',
                        name: 'lobby',
                        props: { default: route => ({ ...route.params, ...route.query }) },
                        components: {
                            default: Lobby
                            //jackpots: JackpotsBar,
                            //balance: BalanceWidget
                        }
                    },
                    {
                        path: '/info',
                        name: 'info',
                        component: Info,
                        redirect: '/info/public-offer',
                        props: true,
                        children: [
                            {
                                path: 'public-offer',
                                name: 'public-offer',
                                component: PublicOffer
                            },
                            {
                                path: 'casino-license',
                                name: 'casino-license',
                                component: CasinoLicense
                            },
                            {
                                path: 'lottery-license',
                                name: 'lottery-license',
                                component: LotteryLicense
                            },
                            {
                                path: 'sports-betting',
                                name: 'sports-betting',
                                component: SportsBetting
                            }
                        ]
                    }
                ]
            },
            {
                path: '/',
                component: GameLayout,
                props: { token: true },
                children: [
                    {
                        path: '/game',
                        props: { default: route => ({ ...route.query }) },
                        components: {
                            default: Game
                        },
                        children: [
                            {
                                path: 'redpipe/:gameId',
                                name: 'game-redpipe',
                                meta: { gameProvider: 'redpipe' },
                                props: route => ({ gameId: route.params.gameId, gameProvider: 'redpipe' }),
                                component: GameRedpipe
                            },
                            {
                                path: 'flg/:gameId',
                                name: 'game-flg',
                                meta: { gameProvider: 'flg' },
                                props: route => ({ gameId: route.params.gameId, gameProvider: 'flg' }),
                                component: GameFlg
                            },
                            {
                                path: 'hdraces/:gameId',
                                name: 'game-hdraces',
                                meta: { gameProvider: 'hdraces' },
                                props: route => ({ gameId: route.params.gameId, gameProvider: 'hdraces' }),
                                component: GameHdRaces
                            },
                            {
                                path: 'aviatrix/',
                                name: 'game-aviatrix',
                                meta: { gameProvider: 'aviatrix' },
                                props: true,
                                component: GameAviatrix
                            }
                        ]
                    }
                ]
            },
            {
                name: 'admin',
                path: '/admin',
                redirect: async to => {
                    console.log('opening admin', state);
                    try {
                        await closeBillAcceptor();
                    } finally {
                        // note: timeout to read msg after ba closed
                        setTimeout(() => {
                            window.location.replace(`http://localhost:5066?token=${to.params.token}`);
                        }, 200);
                    }
                }
            },
            { path: '*', redirect: '/' }
        ]
    });

    router.beforeEach(async (to, from, next) => {
        let handled = false;
        for (let handler of routeHandlers) {
            handled = await handler.handle(state, to, from, next);
            if (handled) return;
        }
        next();
    });

    router.afterEach((to, from) => {
        console.log(`[nav] ${from.path}${JSON.stringify(from.query)}${from.hash} -> ${to.path}${JSON.stringify(to.query)}${to.hash}`);
        if (to.hash) {
            const modalName = to.hash.substr(1);
            state.store.dispatch('modals/show', modalName);
        } else {
            state.store.dispatch('modals/close');
        }
    });

    return router;
}



// WEBPACK FOOTER //
// ./skins/xkiosk/router.js